import { useMemo } from 'react';
import { Card, CardContent, Typography, Box, Grid, CircularProgress, Tooltip as MuiTooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { useExperimentalFeatureUsage } from '../../hooks/useExperimentalFeatureUsage';
import { EXPERIMENTAL_FEATURES } from '../../constants/experimentalFeatures';

const BAR_COLOR = '#7B1FA2';

interface ChartRow {
  featureId: string;
  name: string;
  usersEnabled: number;
  pctOfActiveUsers: number;
}

const AdoptionTooltip = ({ active, payload }: any) => {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload as ChartRow;
  return (
    <Box sx={{ p: 1.5, bgcolor: 'background.paper', borderRadius: '10px', border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
      <Typography variant="body2" fontWeight="medium">{row.name}</Typography>
      <Typography variant="caption" color="text.secondary">
        {row.usersEnabled.toLocaleString()} users ({row.pctOfActiveUsers.toFixed(1)}% of active users)
      </Typography>
    </Box>
  );
};

const ExperimentalFeatureUsageCard = () => {
  const { data, loading, error } = useExperimentalFeatureUsage();

  const chartData = useMemo<ChartRow[]>(() => {
    if (!data?.featureBreakdown) return [];
    return data.featureBreakdown.map((f) => {
      const def = EXPERIMENTAL_FEATURES.find((d) => d.id === f.featureId);
      return { featureId: f.featureId, name: def?.label ?? f.featureId, usersEnabled: f.usersEnabled, pctOfActiveUsers: f.pctOfActiveUsers };
    });
  }, [data]);

  const mostAdopted = chartData[0] ?? null;
  const chartHeight = Math.max(200, chartData.length * 48);

  if (loading) {
    return (
      <Card sx={{ p: 2, height: '100%' }}>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card sx={{ p: 2, height: '100%' }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" mb={2}>Experimental Feature Adoption</Typography>
          <Typography color="error">{error || 'No data available'}</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ p: 2, height: '100%' }}>
      <CardContent>
        <Box display="flex" alignItems="center" gap={1} mb={3}>
          <Typography variant="h6" fontWeight="bold">Experimental Feature Adoption</Typography>
          <MuiTooltip title="Current snapshot of who has each experimental flag enabled. Flags carry no toggle history yet, so this doesn't show a trend over time.">
            <InfoOutlinedIcon fontSize="small" color="action" />
          </MuiTooltip>
        </Box>

        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 'medium' }}>
              Active Users
            </Typography>
            <Typography variant="h5" fontWeight="medium">{data.totalUsers.toLocaleString()}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 'medium' }}>
              Most Adopted
            </Typography>
            <Typography variant="h5" fontWeight="medium">{mostAdopted ? mostAdopted.name : '—'}</Typography>
          </Grid>
        </Grid>

        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart data={chartData} layout="vertical" margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e8eaf0" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={{ stroke: '#cbd5e1' }} allowDecimals={false} />
              <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 12, fill: '#334155' }} tickLine={false} axisLine={false} />
              <Tooltip content={<AdoptionTooltip />} cursor={{ fill: 'rgba(148, 163, 184, 0.08)' }} />
              <Bar dataKey="usersEnabled" name="Users Enabled" radius={[0, 8, 8, 0]} maxBarSize={28}>
                {chartData.map((entry) => (
                  <Cell key={entry.featureId} fill={BAR_COLOR} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Typography color="text.secondary">No experimental features are enabled for any user yet.</Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default ExperimentalFeatureUsageCard;
